import { BetaSignup, ExportField } from '../types';

// Service for handling data exports
export class ExportService {
  // Export selected signups to CSV
  static exportSelectedToCSV(
    signups: BetaSignup[], 
    selectedIds: string[], 
    selectedFields: ExportField,
    type: 'selected' | 'all' = 'all'
  ): void {
    const selectedSignups = signups.filter(signup => 
      selectedIds.includes(signup.id)
    );

    this.exportSignupsToCSV(selectedSignups, selectedFields, type);
  }

  // Export all signups to CSV
  static exportAllToCSV(
    signups: BetaSignup[], 
    selectedFields: ExportField
  ): void {
    this.exportSignupsToCSV(signups, selectedFields, 'all');
  }

  // Export signups in Apple Connect format
  static exportAppleConnectCSV(signups: BetaSignup[]): void {
    const csvRows = signups.map(signup => {
      const nameParts = (signup.name || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      const email = signup.email || '';
      
      return `"${firstName}","${lastName}","${email}"`;
    });

    const csvContent = csvRows.join('\n');
    this.downloadCSV(csvContent, `apple-connect-beta-testers-${this.getDateString()}.csv`);
  }

  // Export selected signups with just email and name (like TestFlight)
  static exportSelectedSimpleCSV(signups: BetaSignup[], selectedIds: string[]): void {
    const selectedSignups = signups.filter(signup => 
      selectedIds.includes(signup.id)
    );

    const csvContent = selectedSignups.map(signup => {
      const name = signup.name || '';
      const email = signup.email || '';
      return `"${email}","${name}"`;
    }).join('\n');

    this.downloadCSV(csvContent, `beta-signups-selected-${this.getDateString()}.csv`);
  }

  // Main export function
  private static exportSignupsToCSV(
    signups: BetaSignup[], 
    selectedFields: ExportField,
    type: 'selected' | 'all'
  ): void {
    const headers = this.getSelectedHeaders(selectedFields);
    
    // For selected export, use Apple Connect format (no headers, no quotes)
    if (type === 'selected') {
      const csvRows = signups.map(signup => 
        this.getSelectedFields(signup, selectedFields).join(',')
      );

      const csvContent = csvRows.join('\n');
      this.downloadCSV(csvContent, `beta-signups-selected-${this.getDateString()}.csv`);
      return;
    }

    // For all other exports, use standard format with headers and quotes
    const csvRows = [
      headers.join(','),
      ...signups.map(signup => 
        this.getSelectedFields(signup, selectedFields)
          .map(field => `"${field}"`)
          .join(',')
      )
    ];

    const csvContent = csvRows.join('\n');
    this.downloadCSV(csvContent, `beta-signups-${type}-${this.getDateString()}.csv`);
  }

  // Get headers based on selected fields
  private static getSelectedHeaders(selectedFields: ExportField): string[] {
    const fieldNames = {
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      platform: 'Platform',
      experienceYears: 'Experience Years',
      timestamp: 'Signup Date',
      status: 'Status',
      testflightEmail: 'TestFlight Email',
      notes: 'Notes'
    };

    return Object.entries(selectedFields)
      .filter(([_, selected]) => selected)
      .map(([field, _]) => fieldNames[field as keyof typeof fieldNames] || field);
  }

  // Get field values for a signup
  private static getSelectedFields(signup: BetaSignup, selectedFields: ExportField): string[] {
    return Object.entries(selectedFields)
      .filter(([_, selected]) => selected)
      .map(([field, _]) => {
        let value = '';
        
        if (field === 'firstName' || field === 'lastName') {
          // Handle the single name field by splitting it
          const nameParts = (signup.name || '').split(' ');
          if (field === 'firstName') {
            value = nameParts[0] || '';
          } else {
            value = nameParts.slice(1).join(' ') || '';
          }
        } else if (field === 'timestamp') {
          value = signup.timestamp ? new Date(signup.timestamp).toLocaleDateString() : '';
        } else {
          value = signup[field as keyof BetaSignup] || '';
        }
        
        return value;
      });
  }

  // Download CSV file
  private static downloadCSV(csvContent: string, filename: string): void {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  // Get current date string for filename
  private static getDateString(): string {
    return new Date().toISOString().split('T')[0];
  }
}
